import { selectedRoaster, session, userProfile } from '$lib/PersistedState.svelte';
import {
  floatToDecimal,
  formatTime,
  getCelsiusIncrementalConverter,
  getHeatingRateConverter,
  getIncrementalTempConverter,
  getTemperatureConverter,
  isNumber
} from '$lib/RLogUtils';
import logger from '$lib/logger';
import { calculateHeatingRate } from '$lib/roasting/heatingRate';
import {
  TemperatureScale,
  type NewRoastEvent,
  type Nullable,
  type TempReading,
  type TimeTempPair
} from '$lib/types/RoastLog';

import { liveStream } from '$lib/liveStream/LiveStream';
import { streamingState } from '$lib/liveStream/streamingState.svelte';
import ActiveRoast from '$lib/stateManagement/activeRoast';
import ChartData from '$lib/stateManagement/chartData';

export type CurrentTempReading = [number, TempReading, TempReading, TempReading];

// All of the events that happen during a roast
export const newRoastEvents = $state<{ current: NewRoastEvent[] }>({ current: [] });

export const initialDataLoad = $state({ current: false });

export const roastNotes = $state({ current: '' });

export const modbusDebugEnabled = $state<{ current: boolean; startTime: Nullable<number> }>({
  current: false,
  startTime: null
});

// Types
type HeatingRate = {
  original: Nullable<number>;
  converted: Nullable<number>;
};

type Readings = {
  beanTemp: Nullable<string>;
  tc2Temp: Nullable<string>;
  tc3Temp: Nullable<string>;
  tc4Temp: Nullable<string>;
  burner: Nullable<number>;
  timer: string;
  secondaryTimer: Nullable<string>;
  developmentRatio: Nullable<string>;
  heatingRate: HeatingRate;
};

const DEFAULT_READINGS_STATE: Readings = {
  beanTemp: null,
  tc2Temp: null,
  tc3Temp: null,
  tc4Temp: null,
  burner: null,

  timer: '-',
  secondaryTimer: null,
  developmentRatio: null,

  heatingRate: {
    original: null,
    converted: null
  }
};

class GlobalState {
  // A map of what TCs are enabled/disabled. Enabled is true.
  tcStateMap = [true, false, false, false];

  // This a reading for one tick. The temperatures in here should always be in Celcius. This is NOT
  // reactive state since we need to set this every reading, but not necessarily update the UI when
  // this changes.
  #rawTemps = <CurrentTempReading>[0, null, null, null];

  // Total seconds that have elapsed during a roast.
  elapsedSeconds = <Nullable<number>>null;

  // The elapsed seconds when the secondary timer started
  secondaryStartTime = <Nullable<number>>null;

  // The timestamp when someone clicked record. When this is null, effectively we're not recording.
  // Once this is set it should not be reset until a new roast, since it's used to calculate
  // elapsedSeconds
  #recordingStartTime = <Nullable<number>>null;

  // A wrapper around the data that is used for chart. The methods on this class are designed to fit
  // with the d3 chart.
  #chartData: ChartData;

  // A wrapper around the time/temperature data that should be sent to the API. This is merely to
  // keep the same data used in the chart in a format that is easy for uploading.
  #activeRoast: ActiveRoast;

  // State that affects UI updates
  //
  // The point-in-time temperature, burner, and sensor readings. These are refreshed whenever there
  // is an update which is driven by the setTemps function, used by thee device classes.
  readings: Readings = $state({ ...DEFAULT_READINGS_STATE });

  // Whether the device is connected
  #deviceConnected = $state(false);

  // Whether the device is in the middle of connected, but not connected
  deviceConnecting = $state(false);

  // Global recording state
  #isRecording = $state(false);

  // Manage first crack as another reactive state, which makes the UI updates easier to manage.
  firstCrackStarted = $state(false);

  constructor() {
    this.#activeRoast = new ActiveRoast();
    this.#chartData = new ChartData();
  }

  get rawTemps() {
    return this.#rawTemps;
  }

  /**
   * This is the primary function that is responsible for setting the current temperature,
   * regardless of roasting state. This needs to get all of the data read to display and update the
   * reactive data for UI updates.
   */
  setTemps(currentTemps: CurrentTempReading, burner: Nullable<number>, timestamp: number) {
    this.#rawTemps = currentTemps.map((t) => Number(t?.toFixed(1))) as CurrentTempReading;
    const convertedTemps = this.convertTemperatures(currentTemps);
    const heatingRate = this.buildHeatingRate(currentTemps[0]);

    const displayTemps: Nullable<string>[] = convertedTemps.map((r) => {
      if (r === null) return null;
      return r <= 0 ? null : r.toFixed(1);
    });

    let secondaryTimer = null;
    let developmentRatio = null;

    if (this.#isRecording) {
      // Update the elapsed time, which is used in the temperature settings to update the state and
      // UI.
      this.elapsedSeconds = Math.round((timestamp - this.#recordingStartTime!) / 1000);

      // When we're recording, push the current data into other state.
      this.recordTick(this.elapsedSeconds, convertedTemps);

      if (this.secondaryStartTime) {
        const timeDiff = this.elapsedSeconds - this.secondaryStartTime;
        secondaryTimer = formatTime(timeDiff);
        const ratio = (timeDiff * 100) / this.elapsedSeconds;
        developmentRatio = ratio.toFixed(1);
      }
    }

    this.readings = {
      beanTemp: displayTemps[0],
      tc2Temp: displayTemps[1],
      tc3Temp: displayTemps[2],
      tc4Temp: displayTemps[3],
      burner: burner !== undefined && burner !== null ? floatToDecimal(burner) : null,
      timer: this.buildTimer(this.elapsedSeconds),
      secondaryTimer,
      developmentRatio,
      heatingRate
    };

    // Publish to the live stream (fire-and-forget, no-op if not connected)
    liveStream.publish({
      beanTemp: displayTemps[0],
      tc2Temp: displayTemps[1],
      tc3Temp: displayTemps[2],
      tc4Temp: displayTemps[3],
      burner: burner !== undefined && burner !== null ? floatToDecimal(burner) : null,
      heatingRate: heatingRate.converted,
      timer: this.buildTimer(this.elapsedSeconds),
      isRecording: this.#isRecording,
      elapsedSeconds: this.elapsedSeconds,
      timestamp
    });
  }

  get deviceConnected() {
    return this.#deviceConnected;
  }

  set deviceConnected(isConnected: boolean) {
    this.#deviceConnected = isConnected;
    this.deviceConnecting = false;

    logger.info(`Device connected: ${isConnected}`, { isConnected, isRecording: this.isRecording });

    // Manage the live stream connection based on device connectivity and user toggle
    streamingState.onDeviceConnectionChange(isConnected);

    // If the device loses connectivity, stop recording. This method will handle all of the gory
    // details of resetting state.
    if (isConnected === false && this.isRecording) {
      this.stopRecording();
    }
  }

  get isRecording() {
    return this.#isRecording;
  }

  set isRecording(value: boolean) {
    this.#isRecording = value;
    if (value === false) {
      this.secondaryStartTime = null;
    }
  }

  get recordedTemps() {
    if (this.#activeRoast) {
      return this.#activeRoast.temps;
    }
    return [[], [], [], [], []];
  }

  get recordedTimes() {
    if (this.#activeRoast) {
      return this.#activeRoast.times;
    }
    return [];
  }

  get recordedBurner(): TimeTempPair[] {
    if (this.#chartData) {
      return this.#chartData.burner;
    }
    return [] as TimeTempPair[];
  }

  getBeanTempAtTime(time: number) {
    const ttPair = this.#chartData.roast[0].find((t: TimeTempPair) => t[0] === time);
    if (ttPair) {
      return ttPair[1];
    }
    return null;
  }

  startRecording(startTime: number) {
    this.#recordingStartTime = startTime;
    this.#isRecording = true;
    logger.info('Recording started', { isRecording: this.isRecording });
  }

  stopRecording() {
    this.#recordingStartTime = null;
    this.#isRecording = false;
    logger.info('Recording stopped');
  }

  startSecondaryTimer() {
    this.secondaryStartTime = this.elapsedSeconds;
    this.firstCrackStarted = true;
    logger.info('Secondary timer started');
  }

  reset() {
    this.#rawTemps = [0, null, null, null];
    this.readings.burner = null;
    this.#recordingStartTime = null;
    this.firstCrackStarted = false;

    this.#activeRoast!.reset();
    this.#chartData.reset(
      selectedRoaster.tcSettings.current[1].active,
      selectedRoaster.tcSettings.current[2].active,
      selectedRoaster.tcSettings.current[3].active
    );
  }

  resetFirstCrack() {
    this.secondaryStartTime = null;
    this.readings.developmentRatio = null;
    this.firstCrackStarted = false;
  }

  /**
   * Record one tick, which should be 1 seconds since the last.
   *
   * This function will perform some minor checks and persist the point-in-time data into the
   * persisted states for the chart and the "current roast", which is used during uploading.
   *
   * @param elapsedSeconds Number of seconds that have elapsed since the last tick.
   * @returns  void
   */
  private recordTick(elapsedSeconds: number, convertedTemps: CurrentTempReading): void {
    // First update the chart
    this.updateChartState(elapsedSeconds, convertedTemps);

    // Manually take care of the bean temp, b/c if that isn't working, we don't want to save
    // anything else. If the bean temp isn't correct bail out.
    if (!isNumber(this.#rawTemps[0])) {
      logger.error('Bean temp is not a number, aborting recording tick.', {
        beanTemp: this.#rawTemps[0]
      });
      return;
    }

    // Now update the active roast container, use for uploading.
    this.updateActiveRoastState(elapsedSeconds);
  }

  /**
   * This method handles the most complex parts of the state, since it needs to do some
   * transformations, update the state, and finally use a Svelte store that is used as a way to
   * trigger chart updates.
   *
   * The store is used because $effect wasn't working as hoped, and stores are very clear in how
   * they react and update through the subscribe method.
   *
   * @param elapsedSeconds Number of seconds since the last tick. Delta should be 1s.
   * @param convertedTemps Temperatures converted to the user's units of choice
   */
  private updateChartState(elapsedSeconds: number, convertedTemps: CurrentTempReading) {
    const convertedTimeTemps = convertedTemps.map((temp: TempReading, i: number) =>
      this.tcStateMap[i] ? ([elapsedSeconds, temp] as TimeTempPair) : null
    );

    // Get TCs 1,2,3 and 4. The Last one is the heating rate.
    const temps = convertedTimeTemps.slice(0, 4);
    // Make sure that each element is a number to avoid pushing NaN into the roast chart. Note that
    // we're dealing with converted temps here.
    temps.forEach((ttPair: Nullable<TimeTempPair>, index: number) => {
      if (ttPair && isNumber(...ttPair)) {
        this.#chartData.addReadingByIndex(index, ttPair);
      }
    });

    // Push the heating rate
    if (isNumber(this.readings.heatingRate.converted)) {
      this.#chartData.addHeatingRate([elapsedSeconds, this.readings.heatingRate.converted!]);
    }

    // Update the burner chart data
    if (this.readings.burner) {
      const lastItem = this.#chartData.burner.pop();
      if (!lastItem) {
        this.#chartData.burner = [
          [elapsedSeconds, this.readings.burner],
          [elapsedSeconds, this.readings.burner]
        ];
      } else if (lastItem[1] != this.readings.burner) {
        this.#chartData.burner.push(lastItem);
      }
      this.#chartData.burner.push([elapsedSeconds, this.readings.burner]);
    }

    // TODO - FIXME
    // After the temperatures and burner have been updated, call store which updates the writeable
    // stores. THIS FEELS JANKY. I'm using stores because they are easier to subscribe to and manage
    // updates, vs state effect. The $effect was not updating as expected. I was hitting a very
    // strange issue where it was triggering as expected with a console.log of the chart data, and
    // without that, not firing.
    this.#chartData.store();
  }

  private updateActiveRoastState(elapsedSeconds: number) {
    // Here, make sure to only push a number if the TC is enabled. Note we're dealing with
    // raw/unconverted temperatures here.
    this.#rawTemps.forEach((temperature: Nullable<number>, i: number) => {
      if (this.tcStateMap[i] && isNumber(temperature)) {
        this.#activeRoast.addTemperatureByIndex(i, temperature!);
      }
    });

    if (isNumber(this.readings.heatingRate.original)) {
      this.#activeRoast.addHeatingRate(this.readings.heatingRate.original!);
    }
    this.#activeRoast.addTime(elapsedSeconds);
  }

  private buildTimer(elapsedSeconds: Nullable<number>) {
    if (elapsedSeconds) {
      return formatTime(elapsedSeconds);
    }
    return '00:00';
  }

  // Helper to convert temperatures
  private convertTemperatures(temps: CurrentTempReading) {
    const converter = converters.tempConverter;

    return temps.map((reading) => {
      if (reading) {
        return parseFloat(converter(reading).toFixed(1));
      } else {
        return null;
      }
    }) as CurrentTempReading;
  }

  private buildHeatingRate(currentTemp: number) {
    let heatingRate = null;
    let convertedHeatingRate = null;

    if (this.isRecording) {
      heatingRate = parseFloat(calculateHeatingRate(currentTemp).toFixed(1));
      convertedHeatingRate = parseFloat(converters.rateConverter(heatingRate).toFixed(1));
    }

    return {
      original: heatingRate,
      converted: convertedHeatingRate
    };
  }
}
export const globalState = new GlobalState();

// Temperature converter state. This shouldn't change often, or ever.
// Export a function that returns the current derived value instead of exporting derived state directly
const converters = $derived.by(() => {
  let tempScale = TemperatureScale.Fahrenheit;

  if (userProfile?.current) {
    tempScale = userProfile.current
      ? userProfile.current.profile.temperature_scale
      : TemperatureScale.Fahrenheit;
  }
  return {
    tempConverter: getTemperatureConverter(tempScale),
    tempToCelsiusConverter: getCelsiusIncrementalConverter(tempScale),
    incrementalConverter: getIncrementalTempConverter(tempScale),
    rateConverter: getHeatingRateConverter(tempScale)
  };
});

// Export a function that returns the current derived value
export function getTempConverters() {
  return converters;
}
